import React, { useMemo } from 'react';
import { Popover, TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TickIcon from 'src/assets/images/icons/tick-grey.svg';
import { IShipmentOption, IAccordionGroup } from './types';
import { useShipmentSelector } from '../../ShipmentSelector';

interface ShipmentDropdownPopoverProps {
  dropdown: {
    isOpenOptions: boolean;
    optionsEl: HTMLButtonElement | null;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    expandedAccordion: string | false;
    filteredOptions: IShipmentOption[];
    handleCloseOptions: () => void;
    handleAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  };
  actions: {
    onSelectShipment: (value: string) => void;
  };
}

export const ShipmentDropdownPopover: React.FC<ShipmentDropdownPopoverProps> = ({ dropdown, actions }) => {
  // Get shipmentInvoiceId from hook
  const { shipmentInvoiceId } = useShipmentSelector();

  const { isOpenOptions, optionsEl, searchTerm, setSearchTerm, expandedAccordion, filteredOptions, handleCloseOptions, handleAccordionChange } = dropdown;
  const { onSelectShipment } = actions;
  // Group options by their section headers
  const groupedOptions = useMemo(() => {
    const groups: IAccordionGroup[] = [];
    let currentGroup: IAccordionGroup | null = null;

    filteredOptions?.forEach((option) => {
      if (option?.isLabel) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          header: option,
          items: [],
        };
      } else if (currentGroup) {
        currentGroup.items.push(option);
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  }, [filteredOptions]);

  return (
    <Popover
      open={isOpenOptions}
      anchorEl={optionsEl}
      onClose={handleCloseOptions}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      sx={{
        '& .MuiPaper-root': {
          width: '310px',
        },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className='sticky top-0 z-10 px-8 xl:px-12 pt-12 pb-12 mb-4 border-b border-neutral-20 bg-white'>
        <TextField
          size='small'
          autoFocus
          placeholder='Search shipment'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
            className: 'text-12 xl:text-16',
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.75rem', xl: '1rem' },
            },
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key !== 'Escape' && e.key !== 'Tab') {
              e.stopPropagation();
            }
          }}
        />
      </div>

      {groupedOptions.map((group) => (
        <Accordion
          key={group.header.value}
          expanded={expandedAccordion === group.header.value}
          onChange={handleAccordionChange(group.header.value)}
          disableGutters
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              minHeight: 'auto',
              padding: '4px 8px',
            },
            '& .MuiAccordionDetails-root': {
              padding: '0px 8px 8px 4px',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${group.header.value}-content`}
            id={`panel-${group.header.value}-header`}
            sx={{
              borderBottom: '1px solid #f0f0f0',
              '& .MuiAccordionSummary-content': {
                margin: '4px 0',
              },
            }}
          >
            {group.header.label}
          </AccordionSummary>
          <AccordionDetails
            sx={{
              maxHeight: '305px',
              overflow: 'auto',
              padding: '0',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#DFDFDF',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            <div className='accordion-items-container'>
              {group.items.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  className={`accordion-item ${option.type === 'PROCESSING' ? 'disabled' : ''}`}
                  onClick={() => {
                    if (option.type !== 'PROCESSING') {
                      onSelectShipment(option.value);
                      handleCloseOptions();
                    }
                  }}
                  disabled={option.type === 'PROCESSING'}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 8px',
                    cursor: option.type === 'PROCESSING' ? 'not-allowed' : 'pointer',
                    opacity: option.type === 'PROCESSING' ? 0.6 : 1,
                    borderBottom: '1px solid #f5f5f5',
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ flex: 1 }}>{option.label}</div>
                  {option.value === shipmentInvoiceId && <img src={TickIcon} alt='TickIcon' />}
                </button>
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </Popover>
  );
};
